export interface IColors {
  primary: string;
  secondary: string;
  tertiary: string;
  black: string;
  white: string;
  body: string;
  success: string;
  successAction: string;
  info: string;
  infoAction: string;
  warning: string;
  warningAction: string;
  error: string;
  errorAction: string;
  background: string;
  textField: string;
  borderColor: string;
}

export interface ITypography {
  baseFontSize: string;
}

export interface ISpacing {
  xSmall: string;
  small: string;
  medium: string;
  large: string;
  xLarge: string;
  xxLarge: string;
  xxxLarge: string;
}

export interface IButtonTheme {
  background: string;
  backgroundHover: string;
  border: string;
  color: string;
  colorHover: string;
  borderHover?: string;
}

export interface IModalTheme {
  backdropColor: string;
}

export interface IThemeBreakpoints {
  smallMobile: number;
  mobile: number;
  tablet: number;
  laptop: number;
  desktop: number;
  largeDesktop: number;
}

export interface ILayout {
  maxWidth: string;
  background: string;
}

export interface ITheme {
  colors: IColors;
  button: {
    primary: IButtonTheme;
    secondary: IButtonTheme;
    tertiary: IButtonTheme;
    quaternary: IButtonTheme;
    quinary: IButtonTheme;
    icon: IButtonTheme;
    text: IButtonTheme;
  };
  typography: ITypography;
  spacing: ISpacing;
  modal: IModalTheme;
  breakpoints: IThemeBreakpoints;
  layout: ILayout;
}

const colors: IColors = {
  primary: 'var(--x-accent-1-1)',
  secondary: 'var(--x-accent-2-1)',
  tertiary: 'var(--x-accent-3-1)',
  black: 'var(--x-black)',
  white: 'var(--x-white)',
  body: 'var(--x-text-color-1)',
  error: 'var(--x-status-error)',
  errorAction: 'var(--x-status-error-action)',
  warning: 'var(--x-status-warning)',
  warningAction: 'var(--x-status-warning-action)',
  success: 'var(--x-status-success)',
  successAction: 'var(--x-status-success-action)',
  info: 'var(--x-status-info)',
  infoAction: 'var(--x-status-info-action)',
  background: 'var(--x-bg-5)',
  textField: 'var(--x-text-color-1)',
  borderColor: 'var(--x-border-1)',
};

const theme: ITheme = {
  colors,
  button: {
    primary: {
      background: 'var(--x-accent-1-1)',
      backgroundHover: 'var(--x-accent-1-action)',
      color: '#FFFFFF',
      colorHover: 'revert',
      border: 'transparent',
      borderHover: 'transparent',
    },
    secondary: {
      background: 'var(--x-bg-1)',
      backgroundHover: colors.primary,
      color: colors.primary,
      colorHover: 'var(--x-text-color-3)',
      border: colors.primary,
      borderHover: colors.primary,
    },
    tertiary: {
      background: 'transparent',
      backgroundHover: 'transparent',
      color: 'rgba(0,0,0,0.6)',
      colorHover: 'rgba(0,0,0,0.6)',
      border: 'rgba(0,0,0,0.6)',
      borderHover: 'rgba(0,0,0,0.6)',
    },
    quaternary: {
      background: 'var(--x-bg-1)',
      backgroundHover: 'var(--x-accent-1-6)',
      border: 'var(--x-bg-9)',
      color: 'var(--x-black-1)',
      colorHover: 'var(--x-accent-1-1)',
      borderHover: 'var(--x-accent-1-1)',
    },
    quinary: {
      background: 'var(--x-bg-5)',
      backgroundHover: 'var(--x-bg-11)',
      border: 'transparent',
      color: 'var(--x-text-color-4)',
      colorHover: 'var(--x-accent-1-1)',
      borderHover: 'transparent',
    },
    icon: {
      background: 'transparent',
      backgroundHover: 'transparent',
      color: '#949494',
      colorHover: 'var(--x-black)',
      border: 'transparent',
    },
    text: {
      background: 'transparent',
      backgroundHover: 'transparent',
      color: 'var(--x-accent-1-1)',
      colorHover: 'var(--x-accent-1-1)',
      border: 'transparent',
    },
  },
  modal: {
    backdropColor: 'var(--x-bg-modal)',
  },
  spacing: {
    xSmall: '4px',
    small: '8px',
    medium: '12px',
    large: '16px',
    xLarge: '20px',
    xxLarge: '24px',
    xxxLarge: '32px',
  },
  typography: {
    baseFontSize: '16px',
  },
  breakpoints: {
    smallMobile: 320,
    mobile: 480,
    tablet: 768,
    laptop: 1024,
    desktop: 1280,
    largeDesktop: 1440,
  },
  layout: {
    maxWidth: '1360px',
    background: 'var(--x-bg-5)',
  },
};

export default theme;
